'use client';

import { useCallback, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { X } from 'lucide-react';
import { getPostsByTag } from '@/lib/wordpress';
import { useTranslations } from 'next-intl';
import PostSkeleton from '@/components/ui/post-tag-skeleton';

interface Tag {
    id: number;
    name: string;
    slug: string;
}

interface Post {
    id: number;
    title: {
        rendered: string;
    };
    excerpt: {
        rendered: string;
    };
    slug: string;
    _embedded?: {
        'wp:featuredmedia'?: Array<{
            source_url?: string;
            alt_text?: string;
            media_details?: {
                sizes?: {
                    medium?: {
                        source_url?: string;
                    };
                };
            };
        }>;
    };
}

interface PostTagsProps {
    tags: Tag[];
    locale: string;
    baseUrl: string;
}

export default function PostTags({ tags, locale, baseUrl }: PostTagsProps) {
    const t = useTranslations('postsTags');
    const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
    const [relatedPosts, setRelatedPosts] = useState<Post[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchRelatedPosts = useCallback(async (tagId: number) => {
        setIsLoading(true);
        try {
            const posts = await getPostsByTag(tagId.toString(), "6", "1", baseUrl);
            setRelatedPosts(posts);
        } catch (error) {
            console.error('Error fetching related posts:', error);
            setRelatedPosts([]);
        } finally {
            setIsLoading(false);
        }
    }, [baseUrl]);

    const handleTagClick = async (tag: Tag) => {
        setSelectedTag(tag);
        await fetchRelatedPosts(tag.id);
    };

    return (
        <>
            <div className="mx-auto max-w-[50rem] py-6">
                <h3 className="mb-4 text-lg font-semibold">{t("title")}</h3>
                <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                        <button
                            key={tag.id}
                            onClick={() => handleTagClick(tag)}
                            className="rounded-full bg-muted px-4 py-2 text-sm transition-colors hover:bg-primary hover:text-primary-foreground dark:text-slate-900 dark:hover:text-white"
                        >
                            {tag.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Full Screen Modal */}
            {selectedTag && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-background dark:bg-slate-900">
                    <div className="min-h-screen w-full p-4 md:p-6">
                        {/* Modal Header */}
                        <div className="sticky top-0 z-10 flex items-center justify-between bg-background/80 dark:bg-slate-900 py-4 backdrop-blur">
                            <h2 className="text-2xl font-bold">
                                {t("modalTitle")} &quot;{selectedTag.name}&quot;
                            </h2>
                            <button
                                onClick={() => setSelectedTag(null)}
                                className="rounded-full p-2 hover:bg-muted"
                                aria-label="Close modal"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Loading State dengan Skeleton */}
                        {isLoading && (
                            <div className="grid gap-6 pt-4 md:grid-cols-2 lg:grid-cols-3">
                                {[...Array(6)].map((_, index) => (
                                    <PostSkeleton key={index} />
                                ))}
                            </div>
                        )}

                        {/* Posts Grid */}
                        {!isLoading && (
                            <div className="grid gap-6 pt-4 md:grid-cols-2 lg:grid-cols-3">
                                {relatedPosts.map((post) => (
                                    <Link
                                        key={post.id}
                                        href={`/${locale}/posts/${post.slug}`}
                                        onClick={() => setSelectedTag(null)}
                                        className="group overflow-hidden rounded-lg border bg-card transition-colors hover:bg-muted/50 dark:bg-slate-800"
                                    >
                                        {post._embedded?.['wp:featuredmedia']?.[0]?.media_details?.sizes?.medium && (
                                            <div className="relative h-48 w-full overflow-hidden">
                                                <Image
                                                    src={post._embedded['wp:featuredmedia'][0].media_details.sizes.medium.source_url || '/path/to/default/image.jpg'}
                                                    alt={post._embedded['wp:featuredmedia'][0].alt_text || ''}
                                                    fill
                                                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                                                />
                                            </div>
                                        )}
                                        <div className="p-4">
                                            <h3
                                                className="mb-2 line-clamp-2 text-lg font-semibold transition-colors group-hover:text-primary"
                                                dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                                            />
                                            <div
                                                className="line-clamp-3 text-sm text-muted-foreground dark:text-slate-100"
                                                dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }}
                                            />
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}

                        {/* No Results */}
                        {!isLoading && relatedPosts.length === 0 && (
                            <div className="flex h-64 items-center justify-center text-center">
                                <p className="text-muted-foreground">
                                    {t("noPosts")} &quot;{selectedTag.name}&quot;
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}